import { CONFIG, type AbilityId, type Difficulty, type TargetMode, type TowerType } from '../config';
import { juiceHitStopActive } from '../game/juice';
import {
  applyProfilePatch,
  bestFor,
  cloneProfile,
  createDefaultProfile,
  isAbilityUnlocked,
  isDifficultyUnlocked,
  isModeUnlocked,
  isTowerUnlocked,
  loadProfile,
  maxOverloadTier,
  purchaseUnlock,
  saveProfile,
  settleRun,
  type Profile,
  type ProfilePatch,
} from '../game/profile';
import { renderGame } from '../game/render';
import {
  clearSavedRun,
  hasSavedRun as hasSavedRunOnDisk,
  loadSavedRun,
  persistRun,
  relinkSharedPaths,
  shouldPersist,
  type SavedRun,
} from '../game/runSave';
import { createInitialState, startingGoldFor, type GameMode, type GameState } from '../game/state';
import {
  applyPylonAuras,
  canPlace,
  cycleTowerTargetMode,
  genWave,
  pathFrom,
  placeTower,
  recomputePaths,
  sellTower,
  setOverload,
  setTowerTargetMode,
  simTick,
  spawnBoss,
  startWave,
  setOverclock as systemsSetOverclock,
  toggleOverclock as systemsToggleOverclock,
  towerAt,
  upgradeTower,
  useAbility,
} from '../game/systems';
import {
  DEFAULT_MAP_ID,
  activeMap,
  activeMapId,
  getMap,
  inBounds,
  pointToCell,
  setActiveMap,
} from '../game/map';
import { invalidateBackdrop } from '../game/render';
import {
  abilitySlotAt,
  applyLocaleChipClick,
  difficultyToggleAt,
  drawCheatProfilePips,
  drawHud,
  helpButtonAt,
  hudButtonAt,
  localeChipAt,
  modeToggleAt,
  overloadPanelAt,
  selectionPanelButtonAt,
  titleSelectorsDimmed,
  towerOfButton,
  tutorialButtonAt,
  winContinueAt,
  type HudButtonId,
} from '../ui/hud';
import {
  canOpenProfile,
  drawProfileLauncher,
  drawProfileScreen,
  profileLauncherAt,
  profileScreenHit,
  type ProfileTab,
} from '../ui/profileScreen';
import { Onboarding } from '../ui/onboarding';
import { Tutorial } from '../ui/tutorial';
import {
  applyInitialLocale,
  CATALOGUE_BY_ID as i18nCatalogues,
  getLocale,
  LOCALES as i18nLocales,
  setLocale,
  t as ti18n,
} from '../i18n';
import {
  awardPuzzleBest,
  evaluatePuzzleAttempt as evaluatePuzzleAttemptPure,
  loadPuzzleBests,
  loadPuzzleLevel,
  savePuzzleBests,
  type PuzzleOutcome,
} from '../game/puzzle';
import {
  PUZZLES,
  getPuzzleLevel,
  type PuzzlePlacement,
} from '../game/puzzles';
import {
  drawPuzzleBriefing,
  drawPuzzleResult,
  drawPuzzleSelect,
  puzzleBriefingHit,
  puzzleResultHit,
  puzzleSelectHit,
} from '../ui/puzzleSelect';
import {
  acceptGuestMode,
  drawStartScreen,
  isStartScreenOpen,
  signInWithDiscord,
  signInWithEmail,
  signInWithGoogle,
  signOut as signOutAuth,
  startScreenHit,
  type StartHit,
} from '../auth/start';
import {
  acceptCookiesChoice,
  cookiesBannerHit,
  drawCookiesBanner,
  isCookiesBannerOpen,
} from '../auth/cookies';
import { losBlocked, losFirstHitT, packLosWalls } from '../game/los';
import { Audio } from './audio';
import { Input } from './input';
import { GameLoop } from './loop';
import { Renderer } from './renderer';
import { Rng } from './rng';
import type { GameSnapshot, GameTestApi } from './testApi';
import { clamp } from './util';

/**
 * Version reported via the test contract. Bump on contract changes.
 * `1.21.0` — Sprint 63 (puzzle-ux-briefing-s63): puzzle-UX additions —
 * (1) `state.puzzleBriefingPending` snapshot field (the briefing-card
 * modal flag a freshly loaded puzzle level opens with `true`); (2) test
 * hook `dismissPuzzleBriefing()` to flip the flag from QA; (3) puzzle
 * mode now transitions to `phase = 'won' | 'lost'` when the wave
 * terminates — previously it reverted to `'build'` (the legacy
 * behaviour). Per-level briefings are populated on every shipped
 * `PuzzleLevel.briefing`. No breaking changes to Campaign / Endless /
 * Maze / Puzzle simulation surfaces — additive snapshot field + one
 * additive sim transition isolated to puzzle mode. See
 * docs/PUZZLE_DESIGN.md §1.2 and the Sprint 63 brief.
 * `1.20.0` — Sprint 54 (cheat-profile-s54): testing-friendly additions —
 * (1) a `cheatUnlockAll()` test hook that grants every node in
 * `CONFIG.unlocks` and sets `profile.flux = 9999`, also fired on boot when
 * the URL carries `?cheat=unlock-all`; (2) naive multi-profile switching
 * via `?profile=<name>` — when set, profile + run-save persistence uses
 * the localStorage keys `overvolt.profile.<name>` / `overvolt.run.<name>`
 * instead of the default. The default profile (no URL param) keeps the
 * legacy `overvolt.profile` / `overvolt.run` keys (byte-identical
 * Sprint-53 path). See docs/GAME_DESIGN.md §19 / §cheat-profile.
 * `1.19.0` — Sprint 47 (endless-redesign-s47): per-wave Endless **modifier
 * rotation** — `WaveDef.modifier?: WaveModifierId` (additive optional
 * field). A pure function of `(seed, w)`, surfaced via `genWave(w).modifier`
 * for Endless waves 11+ excluding surge / boss-milestone waves; always
 * undefined for Campaign (1–10) and Maze. Campaign waves and the
 * surge / boss code paths are byte-identical pre-/post-S47. Adds one new
 * module `src/game/endless.ts` and one config knob
 * `endlessModifierStartWave`. See docs/ENDLESS_DESIGN.md.
 * `1.18.0` — Sprint 43 (REC 4, map3-s43): third hand-authored map
 * **`'serpentine'`** (Snake Run) — additive only; the existing `default`,
 * `cramped`, `lines` map ids are bit-for-bit Sprint-39. Adds one entry to
 * the public `CONFIG.maps` registry and one path/blocked-tile module in
 * `src/game/map.ts`; the snapshot's `mapId` field gains one additional
 * legal value. See docs/GAME_DESIGN.md §3 and docs/STRATEGY_ANALYSIS.md §6
 * property 4 (chokepoint count / tight serpentine).
 * `1.17.0` — Sprint 39 (REC 3.b, pylon-aura-s39): per-Pylon **fire-rate
 * aura** (additive — a no-Pylon-aura build is bit-for-bit Sprint-31). Adds
 * the derived `Tower.auraRateMult` cache (recomputed by `applyPylonAuras`
 * on every place / sell / upgrade) and three `CONFIG.pylonAura*` tunables.
 * No removals; the snapshot grows one field on `Tower`. See
 * docs/REC3_DESIGN.md §2.B and docs/NEXT_BATCH_PLAN.md §2.6.
 * `1.16.0` — Sprint 31: **Streak** enemy (additive — `'streak'` joins the
 * `EnemyType` union; new `enemies.streak` def, `streakSlowResist` /
 * `streakSprintMult` / `streakSprintRadius` tunables, `endlessThreatCost.streak`
 * + `endlessWeights.streak` records; new `Enemy.isSprinting` boolean read by
 * render + tests). No removals, no breaking shape change to any existing
 * snapshot field. See docs/ENEMY_DESIGN.md.
 * `1.15.0` — Sprint 26 (REC 3 keystone): per-tower **Overclock** (additive —
 * the default-OFF state is a bit-for-bit Sprint-25 game). Adds
 * `toggleOverclock` / `setOverclock` test hooks, the `Tower.overclock`
 * boolean, and the `PowerState.overclockCount` count. See
 * docs/REC3_DESIGN.md §4.
 * `1.14.0` — Sprint 30: puzzle mode (additive — no breaking changes to
 * Campaign / Endless / Maze surface). Adds `setPuzzleLevel`,
 * `evaluatePuzzleAttempt`, `puzzleLevels`, and the `losWalls` /
 * `puzzleLevelId` snapshot fields. See docs/PUZZLE_DESIGN.md.
 * `1.22.0` — Sprint 73 (tutorial-impl-s73): opt-in 12-step interactive
 * tutorial. Adds:
 *   - `startTutorial()` — launch the tour from step 1 (mirrors clicking
 *     the title-screen Start Tutorial button).
 *   - `tutorialNext()` — advance one step from QA (the final step's next
 *     completes the tour and flips `profile.seenFullTutorial`).
 *   - `tutorialSkip()` — abort the tour cleanly and flip the seen flag.
 *   - `tutorial` snapshot — `{ active, step }` for QA reads.
 *   - Profile schema bumps v4 → v5; the new `seenFullTutorial: boolean`
 *     joins the read-only `__GAME__.profile` snapshot. `setProfile` accepts
 *     the field via the patch shape (see `ProfilePatch`).
 * No breaking changes to Campaign / Endless / Maze / Puzzle simulation
 * surfaces — purely additive UI/profile plumbing. See
 * `docs/TUTORIAL_DESIGN.md`.
 */
const CONTRACT_VERSION = '1.22.0';

/**
 * Top-level orchestrator for OVERVOLT.
 *
 * Owns the engine subsystems and the game state, and bridges the engine's
 * fixed 60 Hz step to the design's 10 tps logical tick. The OVERVOLT phase
 * machine (`build / wave / won / lost`) lives entirely in {@link GameState};
 * this class only wires input, simulation and rendering together.
 */
export class Game {
  private readonly renderer: Renderer;
  private readonly input = new Input();
  private readonly audio = new Audio();
  /** Onboarding UI layer (intro, hints, tooltips, Help panel) — UI-only. */
  private readonly onboarding = new Onboarding();
  /**
   * Sprint 73 (tutorial-impl-s73) — the 12-step opt-in interactive tour.
   * Lives alongside `onboarding`: the tour is the "explicit curriculum"
   * layer (player-opted-in), `onboarding` is the "discoverable hints"
   * layer (one-shot toasts). Both share render slot ordering but never
   * fire simultaneously — the tour overlay captures pointer input while
   * active, so the onboarding toast layer underneath is functionally
   * gated by `tutorial.active` (the `Game` render order paints the tour
   * last so it sits on top).
   */
  private readonly tutorial = new Tutorial();
  private readonly rng: Rng;
  private readonly loop: GameLoop;
  private state: GameState = createInitialState();
  private frame = 0;
  /** Current PRNG seed — preserved across an in-place restart. */
  private seed: number;
  /** Run mode — preserved across an in-place restart (R / reset()). */
  private mode: GameMode = 'campaign';
  /** Run difficulty — preserved across an in-place restart (R / reset()). */
  private difficulty: Difficulty = CONFIG.difficulty;
  /** Fixed 60 Hz steps banked toward the next logical tick. */
  private stepAccumulator = 0;
  /**
   * True once the Airstrike ability is armed — the next playfield click picks
   * its target tile (mirrors tower placement mode). `Esc` cancels.
   */
  private pendingAirstrike = false;
  /**
   * Set once `tick()` / `step()` is used. From then on the RAF loop still
   * renders and processes input, but no longer advances the simulation — so a
   * `tick()`-driven test is the sole simulation driver and stays deterministic
   * even across separate `page.evaluate()` calls.
   */
  private loopSuspended = false;
  /**
   * Sprint 54 (cheat-profile-s54) — the active profile name from
   * `?profile=<name>` (sanitised to `[a-z0-9_-]{1,32}`), or `undefined` for
   * the legacy default (un-named) profile. Every `loadProfile` / `saveProfile`
   * / `persistRun` / `clearSavedRun` / `loadSavedRun` / `hasSavedRunOnDisk`
   * call routes through this name so all four (profile blob + run save) live
   * under a per-profile key. A `?profile=alice&cheat=unlock-all` URL writes
   * the cheat into the alice profile only — the default is untouched.
   */
  private readonly profileName: string | undefined = readProfileNameFromUrl();
  /**
   * The persistent meta-progression profile (§19). Lives only in
   * `localStorage`; **never** in `GameState` and never read by the simulation,
   * so the determinism contract is untouched. Owned here, at the composition
   * root, and consulted only by the UI-layer unlock gate.
   */
  private profile: Profile = loadProfile(this.profileName);
  /**
   * Sprint 54 (cheat-profile-s54) — true while the "unlock everything" cheat
   * is active on this profile. Set on boot when `?cheat=unlock-all` is
   * present, and on a runtime call to {@link cheatUnlockAll}. Drives the
   * `🔓 CHEAT` HUD pip; gameplay / sim / determinism unaffected.
   */
  private cheatActive = false;
  /** True while the full-screen Profile / Scoreboard overlay is shown (§19.3). */
  private profileScreenOpen = false;
  /** Which Profile-screen tab is active. */
  private profileTab: ProfileTab = 'unlocks';
  /** Flux the most recently settled run paid out — shown on the game-over screen. */
  private lastRunFlux = 0;
  /**
   * A saved live run discovered at boot — waiting for the player's choice on
   * the title-screen overlay. Until they pick Resume or New, the fresh initial
   * state sits underneath the overlay and the simulation is held still. `null`
   * once there is no saved run (or the player has resolved the prompt).
   */
  private pendingResume: SavedRun | null = null;
  /** Saves are pinned (skipped) while reset / discard is in flight — see `withSavesPinned`. */
  private savesPinned = false;
  /**
   * Sprint 63 (puzzle-ux-briefing-s63) — gold spent on the current puzzle
   * attempt's layout, snapshotted at wave-start so the Win / Lose overlay
   * can show the player a stable "gold spent" stat even though the gold
   * counter itself reads `0` after a tight win. Captured in `runTick` on the
   * build → wave transition; reset by `setPuzzleLevel`.
   */
  private lastPuzzleGoldSpent = 0;
  /** Bound `beforeunload` listener — detached in {@link dispose}. */
  private readonly onBeforeUnload = (): void => this.saveOnPageExit();
  /** Bound `visibilitychange` listener — detached in {@link dispose}. */
  private readonly onVisibilityChange = (): void => {
    if (document.visibilityState === 'hidden') this.saveOnPageExit();
  };

  constructor(private readonly canvas: HTMLCanvasElement) {
    // Resolve the UI locale up-front: `?lang=cs` URL override > navigator
    // language > English fallback. Subsequent `t()` calls (HUD, onboarding,
    // notices) read this once-set value. Sim / determinism is untouched —
    // this is a render-layer setting only (docs/GAME_DESIGN.md §i18n).
    applyInitialLocale();
    this.seed = readSeedFromUrl() ?? CONFIG.seed;
    // Sprint 21 — `?map=<id>` URL override picks the active map up-front, so
    // the fresh initial state below already sees the chosen path / blocked-
    // tile set. Unknown ids silently fall back to the default (`setActiveMap`
    // is forgiving — see `src/game/map.ts`). Saves resume against the saved
    // run's `state.mapId`, not the URL — but that is handled in `applyResume`.
    const mapId = readMapFromUrl() ?? DEFAULT_MAP_ID;
    setActiveMap(mapId);
    this.rng = new Rng(this.seed);
    this.renderer = new Renderer(canvas);
    this.state = createInitialState(this.difficulty, this.mode);
    this.state.seed = this.seed;
    this.applyBests();
    // A live run from a prior page load is dormant until the player picks
    // Resume / New on the title-screen overlay. We probe localStorage up-front
    // so the prompt can render on the very first frame. Sprint 54 — routed
    // through `profileName` so `?profile=alice` resumes alice's run, not the
    // default profile's.
    this.pendingResume = loadSavedRun(this.profileName);
    // Sprint 54 — `?cheat=unlock-all` applies on boot to the active profile
    // (the default if no `?profile=` is set). The grant runs once the rest of
    // the constructor has settled so `this.profile` is already loaded.
    if (readCheatFromUrl()) this.cheatUnlockAll();
    this.loop = new GameLoop(
      {
        update: () => this.fixedUpdate(),
        render: (alpha) => this.render(alpha),
      },
      CONFIG.loop,
    );
  }

  /** Attach input listeners and start the animation loop. Call exactly once. */
  boot(): void {
    this.input.attach(this.canvas);
    // A tab-close or page-refresh during a wave (the most common mid-run reload
    // path) doesn't hit the wave-cleared quiet boundary — so we also dump on
    // `beforeunload` and `visibilitychange→hidden`. Visibility fires reliably
    // on mobile / tab-switch where `beforeunload` does not (Sprint 16).
    window.addEventListener('beforeunload', this.onBeforeUnload);
    document.addEventListener('visibilitychange', this.onVisibilityChange);
    this.loop.start();
  }

  /** Stop the loop and detach listeners. Handy for teardown / tests. */
  dispose(): void {
    this.loop.stop();
    this.input.detach();
    window.removeEventListener('beforeunload', this.onBeforeUnload);
    document.removeEventListener('visibilitychange', this.onVisibilityChange);
  }

  // --- fixed-timestep bridge -------------------------------------------------

  /** Runs `CONFIG.loop.updateHz` (60) times per second. */
  private fixedUpdate(): void {
    this.handleInput();

    // While a test drives the run through tick()/step(), the RAF loop stops
    // advancing the simulation (so it cannot race the test) but keeps handling
    // input — keyboard / mouse tests still work after a tick()-driven setup.
    if (!this.loopSuspended) {
      const s = this.state;
      // The Help panel freezes the live simulation without touching `state`
      // (it is UI-only). Test-driven `tick()` bypasses this gate entirely.
      const simActive =
        (s.phase === 'build' || s.phase === 'wave') && !s.paused && !this.onboarding.helpOpen;
      // §18 hit-stop — a Kernel-beat micro-freeze that briefly withholds tick
      // advancement here in the rAF scheduler (no steps are banked while it is
      // active, so the freeze is real, not a catch-up). The `tick()` test hook
      // drives `advanceTicks` and never reaches this path, so hit-stop is
      // invisible to determinism (docs/GAME_DESIGN.md §18.1.3).
      const hitStopped = CONFIG.juiceEnabled && juiceHitStopActive();
      if (simActive && !hitStopped) {
        this.stepAccumulator += 1;
        const stepsPerTick = this.fixedStepsPerTick();
        while (this.stepAccumulator >= stepsPerTick) {
          this.stepAccumulator -= stepsPerTick;
          this.runTick();
        }
      }
    }

    this.input.endStep();
  }

  /**
   * Advance the simulation by `n` logical ticks under explicit test control.
   * Suspends the RAF loop's own stepping so it cannot race, and drains input
   * each tick so `pressKey` / `releaseKey` are deterministic alongside `tick()`.
   */
  private advanceTicks(n: number): void {
    this.loopSuspended = true;
    for (let i = 0; i < n; i += 1) {
      this.handleInput();
      this.runTick();
      this.input.endStep();
    }
  }

  /** Fixed 60 Hz steps that make up one logical tick at the current speed. */
  private fixedStepsPerTick(): number {
    return CONFIG.loop.updateHz / (CONFIG.tickRate * this.state.speed);
  }

  /** Advance the simulation one logical tick and persist any end-of-run records. */
  private runTick(): void {
    const before = this.state.phase;
    const goldBefore = this.state.gold;
    simTick(this.state, this.rng, this.audio);
    const s = this.state;
    // Sprint 63 (puzzle-ux-briefing-s63) — promote the legacy puzzle-mode
    // `wave → build` transition (the only one `systems.ts/checkWaveEnd`
    // produces in puzzle mode) into a proper `won` transition so the
    // puzzle Win overlay can render and the bests blob picks up the solve.
    // Loss is already handled by `endRun` when `lives <= 0`; we only inject
    // the win path here. Determinism intact — this is post-tick orchestrator
    // work, identical for any given `(level, attempt)` pair.
    if (
      s.mode === 'puzzle' &&
      before === 'wave' &&
      s.phase === 'build' &&
      s.lives > 0
    ) {
      this.lastPuzzleGoldSpent = this.snapshotPuzzleGoldSpent(goldBefore);
      s.phase = 'won';
      s.finalScore = 1000 + s.gold * 5 + s.lives * 50;
      // Award the per-level best — pure update on the persisted blob.
      if (s.puzzleLevelId) {
        const bests = loadPuzzleBests();
        const outcome: PuzzleOutcome = {
          solved: true,
          reason: null,
          ticksUsed: s.tick,
          livesRemaining: s.lives,
          towersUsed: s.towers.length,
          goldSpent: this.lastPuzzleGoldSpent,
          peakDemand: s.power.demand,
        };
        const next = awardPuzzleBest(bests, s.puzzleLevelId, outcome);
        if (next !== bests) savePuzzleBests(next);
      }
      this.audio.play('win');
    }
    if (before !== s.phase) {
      // Any exit from the wave phase (clear, win or loss) disarms a still-armed
      // Airstrike — otherwise a leftover `pendingAirstrike` would silently eat
      // the first build-phase click of the next wave.
      if (before === 'wave') this.pendingAirstrike = false;
      // Sprint 63 — snapshot gold spent at the wave's terminal tick for the
      // Win / Lose overlay (a tight win drains `state.gold` to 0; a loss
      // leaves whatever the attempt didn't spend). For puzzle mode this is
      // already populated by the won branch above; for a lost attempt we
      // capture it here.
      if (s.mode === 'puzzle' && (s.phase === 'won' || s.phase === 'lost')) {
        this.lastPuzzleGoldSpent = this.snapshotPuzzleGoldSpent(goldBefore);
      }
      if (s.phase === 'won' || s.phase === 'lost') {
        this.settleRunIntoProfile();
        // The run is over — wipe the save so a fresh reload lands on the
        // title screen, not the resume prompt for a finished run.
        clearSavedRun(this.profileName);
      } else if (before === 'wave' && s.phase === 'build') {
        // Wave cleared → build phase: the cheapest deterministic quiet moment
        // — no enemies, no projectiles, no spawn director in flight. Snapshot
        // the live run here so a mid-fight reload still leaves a clean resume
        // anchor between waves (Sprint 16).
        this.persistCurrentRun();
      }
    }
  }

  /**
   * Sprint 63 — compute the gold spent on the current puzzle attempt's
   * layout. Uses the loaded level's `goldBudget` as the starting point and
   * subtracts the live `state.gold`; falls back to `goldBefore` (pre-tick
   * gold) on a leak when the wave-end tick happens to flush further gold
   * (no current path does, but the fallback keeps the stat non-negative).
   */
  private snapshotPuzzleGoldSpent(goldBefore: number): number {
    const s = this.state;
    if (!s.puzzleLevelId) return Math.max(0, goldBefore - s.gold);
    const level = getPuzzleLevel(s.puzzleLevelId);
    if (!level) return Math.max(0, goldBefore - s.gold);
    return Math.max(0, level.goldBudget - s.gold);
  }

  /**
   * Settle a just-finished run into the persistent profile (§19.5 / §20): award
   * Flux, append the Scoreboard run record, update bests + lifetime stats. Runs
   * exactly once per run, on the `won` / `lost` transition. The profile is meta
   * — settling it never touches `GameState`, so determinism is intact.
   */
  private settleRunIntoProfile(): void {
    const s = this.state;
    // Sprint 30 — Puzzle-mode runs settle into a separate per-level bests
    // table (see `puzzleSelect.ts` / `awardPuzzleBest`), not the Scoreboard
    // bests grid which keys on `RunMode` (`'campaign' | 'endless' | 'maze'`).
    // The puzzle path branches early so the live Scoreboard / Flux economy
    // is never touched by a puzzle clear.
    if (s.mode === 'puzzle') return;
    const won = s.phase === 'won';
    // `wavesCleared`: a win clears `state.wave`; a loss happens mid-wave, so
    // the waves fully cleared are one fewer than the wave reached.
    const grossWavesCleared = won ? s.wave : Math.max(0, s.wave - 1);
    // A run continued from a prior `won` segment (Continue → Endless, §14) has
    // already settled that segment into the profile — subtract the baseline so
    // this settlement counts only the continuation's OWN waves + score, never
    // the carried-over campaign/maze run (which would double-count Flux, bests
    // and stats — docs/GAME_DESIGN.md §14 / §19.5).
    const wavesCleared = Math.max(0, grossWavesCleared - s.settledWaves);
    const finalScore = Math.max(0, s.finalScore - s.settledScore);
    this.lastRunFlux = settleRun(this.profile, {
      mode: s.mode,
      difficulty: s.difficulty,
      wavesCleared,
      deepestWave: s.wave,
      finalScore,
      won,
      overloadMax: s.overloadMax,
    });
    saveProfile(this.profile, this.profileName);
    this.applyBests();
  }

  /**
   * Refresh the in-`state` high-score / best-wave mirrors from the profile's
   * personal bests for the current mode + difficulty (§20). These snapshot
   * fields stay in `state`; the source of truth is the profile.
   */
  private applyBests(): void {
    // Sprint 30 — puzzle mode has its own per-level bests (Scoreboard `bests`
    // keys on `RunMode` ∈ campaign/endless/maze only); skip the live mirror to
    // keep that path tight to the legacy modes. The highScore / bestWave
    // readout is hidden under puzzle mode anyway (the HUD shows the par-pip
    // row instead — see §puzzle in docs/GAME_DESIGN.md).
    if (this.state.mode === 'puzzle') return;
    const best = bestFor(this.profile, this.state.mode, this.state.difficulty);
    this.state.highScore = best.score;
    this.state.bestWave = best.wave;
  }

  // --- run save / resume (Sprint 16) ----------------------------------------

  /**
   * Snapshot the current run into `localStorage`. Skipped while a resume is
   * pending (the dormant fresh state would clobber the still-undecided save),
   * while a reset is in flight (`savesPinned`), and on the fresh title screen
   * (nothing worth saving — see `runSave.shouldPersist`). Pure side-effect:
   * never mutates `state`. Saving is best-effort — a quota-exceeded
   * `localStorage` never crashes the live run.
   */
  private persistCurrentRun(): void {
    if (this.savesPinned) return;
    if (this.pendingResume) return;
    if (!shouldPersist(this.state)) return;
    persistRun(this.state, this.rng.getState(), this.profileName);
  }

  /**
   * `beforeunload` / `visibilitychange` handler — the page is going away or
   * being backgrounded mid-wave. We save unconditionally (skipping the
   * `pendingResume` guard would be wrong: if the player is mid-decision the
   * existing on-disk save is what they should resume into, not a clobber).
   */
  private saveOnPageExit(): void {
    if (this.pendingResume) return; // keep the on-disk save the player has not chosen on yet
    if (this.savesPinned) return;
    if (!shouldPersist(this.state)) return;
    persistRun(this.state, this.rng.getState(), this.profileName);
  }

  /**
   * Rehydrate the engine from a {@link SavedRun}. Adopts every state field, the
   * mode / difficulty / seed, and the PRNG cursor; relinks shared path refs
   * (fixed-path mode collapses every enemy's path back onto the singleton);
   * refreshes the bests mirror; resets the per-loop step accumulator + any
   * armed Airstrike. Quiet on a malformed blob — the caller must pre-validate
   * via {@link loadSavedRun}. After this returns the live state IS the resumed
   * run; the next render will draw it and the next tick will advance it.
   */
  private applyResume(saved: SavedRun): void {
    const s = saved.state;
    this.state = s;
    this.mode = s.mode;
    this.difficulty = s.difficulty;
    this.seed = s.seed >>> 0;
    this.rng.setState(saved.rngState);
    // Sprint 21 — re-bind the active map FIRST, so `recomputePaths` snaps
    // `state.path` to the saved-run's map's path (an older save without
    // `mapId` defaults harmlessly to `'default'`). An unknown id (a save from
    // a future build) falls back to the default; the player keeps their run.
    const savedMapId = typeof s.mapId === 'string' && s.mapId.length > 0 ? s.mapId : DEFAULT_MAP_ID;
    setActiveMap(savedMapId);
    invalidateBackdrop();
    s.mapId = activeMapId();
    // A non-maze run shares one PATH reference across every enemy — JSON
    // round-trip blew that apart, so re-point them and let `recomputePaths`
    // restore the canonical fixed-path singleton (or rebuild the maze A*).
    relinkSharedPaths(this.state);
    recomputePaths(this.state);
    // Sprint 39 (REC 3.b — docs/REC3_DESIGN.md §2.B): the Pylon-aura cache
    // (`Tower.auraRateMult`) round-trips through JSON, but a forward-version
    // save built before the cache existed could resume with `undefined` —
    // and even a same-version save is cheap to rebuild deterministically
    // from the saved Pylon roster. One pure pass keeps the divisor honest.
    applyPylonAuras(this.state);
    this.applyBests();
    this.state.muted = this.audio.isMuted();
    this.stepAccumulator = 0;
    this.pendingAirstrike = false;
    this.profileScreenOpen = false;
  }

  /**
   * Resolve the title-screen resume prompt — adopt the pending saved run.
   * No-op (returns false) if no save is pending. The save stays on disk: the
   * next quiet boundary (wave-cleared or page exit) will rewrite it from the
   * now-live resumed state, so the disk copy is always at most one wave stale.
   */
  resumeSavedRun(): boolean {
    const saved = this.pendingResume;
    if (!saved) return false;
    this.applyResume(saved);
    this.pendingResume = null;
    return true;
  }

  /**
   * Resolve the title-screen resume prompt — discard the pending saved run.
   * Wipes the on-disk blob and the in-memory pendingResume, then leaves the
   * fresh initial state in place. Idempotent.
   */
  discardSavedRun(): void {
    this.pendingResume = null;
    clearSavedRun(this.profileName);
  }

  /** True while the resume overlay should be shown (an undecided save exists). */
  hasPendingResume(): boolean {
    return this.pendingResume !== null;
  }

  /**
   * Sprint 54 (cheat-profile-s54) — grant every node in `CONFIG.unlocks` to the
   * active profile and set `profile.flux` to a high value (`9999`). Persists
   * the profile immediately so a reload keeps the grant. Side-effects are
   * limited to the profile blob (and `this.cheatActive` for the HUD pip);
   * gameplay, simulation and determinism are untouched — only the UI-only
   * unlock gate (§19.5) is bypassed. Fires on boot when `?cheat=unlock-all`
   * is present, and on demand via `window.__GAME__.cheatUnlockAll()`.
   *
   * Idempotent: a node already in `unlocked` is left in place; the Flux floor
   * is `Math.max(9999, current)` so a player with more than 9999 Flux is not
   * silently downgraded.
   */
  cheatUnlockAll(): void {
    const owned = new Set(this.profile.unlocked);
    for (const node of CONFIG.unlocks) owned.add(node.id);
    this.profile.unlocked = Array.from(owned);
    this.profile.flux = Math.max(9999, this.profile.flux);
    saveProfile(this.profile, this.profileName);
    this.cheatActive = true;
    this.applyBests();
  }

  // --- Sprint 73 (tutorial-impl-s73) ---------------------------------------

  /**
   * Launch the 12-step opt-in tour from step 1 (mirrors a click on the
   * title-screen Start Tutorial button). Resets the run to a fresh wave-0
   * build phase via the tour's host seam first, so the player's "welcome"
   * step always lands on a clean board even if they triggered the tour
   * mid-thought from the title screen (the tour button is gated on the
   * title screen anyway — this is the safety net).
   */
  startTutorial(): void {
    this.tutorial.start({
      getState: () => this.state,
      resetRun: () => this.reset(this.seed),
      persistProfile: () => saveProfile(this.profile, this.profileName),
    });
  }

  /**
   * Advance the tour one step. On the final step's call the tour exits and
   * `profile.seenFullTutorial` flips to `true`. No-op when the tour is
   * inactive — mirrors the in-overlay PLAY / NEXT button click path.
   */
  advanceTutorial(): void {
    this.tutorial.next(this.profile);
  }

  /**
   * Skip the tour cleanly — flip the seen flag and snap the run to a fresh
   * wave-0 build phase. Mirrors the in-overlay SKIP TOUR button click path.
   */
  skipTutorial(): void {
    this.tutorial.skip(this.profile);
  }

  /**
   * Read-only tour state for the test API — exposes `active` and `step`.
   * The class field is private; the getter wraps it for the `__GAME__`
   * snapshot, mirroring the `cheatActive` / `profileName` getters above.
   */
  get tutorialState(): { active: boolean; step: number } {
    return { active: this.tutorial.active, step: this.tutorial.step };
  }

  // --- input ----------------------------------------------------------------

  /** Translate this step's keyboard + pointer input into game actions. */
  private handleInput(): void {
    const input = this.input;
    const s = this.state;

    const hotkeys = [
      '1', '2', '3', '4', '5', '6', '7', 'space', 'f', 'u', 't', 's', 'escape', 'm', 'r', 'h',
      '?', 'q', 'w', 'e', '[', ']', 'p', 'enter', 'o',
    ];
    const anyKey = hotkeys.some((k) => input.wasPressed(k));
    if (anyKey || input.wasClicked()) this.audio.unlock();

    // The Resume prompt claims input absolutely — it must be resolved before
    // any title-screen control (mode toggles, Profile launcher, hotkeys) can
    // run. Enter = Resume, Esc = New; clicks route to the dialog buttons.
    if (this.pendingResume) {
      if (input.wasPressed('enter')) {
        this.resumeSavedRun();
      } else if (input.wasPressed('escape')) {
        this.discardSavedRun();
      } else if (input.wasClicked()) {
        const pos = input.clickPos();
        const hit = resumePromptHit(pos.x, pos.y);
        if (hit === 'resume') this.resumeSavedRun();
        else if (hit === 'discard') this.discardSavedRun();
      }
      return;
    }

    // Sprint 73 (tutorial-impl-s73) — the interactive tour overlay claims all
    // input while active (§4.3 "input is locked to only the elements relevant
    // to the current step"). Esc skips, arrow keys / Enter / Space advance,
    // clicks route to the Back / Skip / Next buttons. Any other gesture is
    // swallowed so the underlying HUD never acts under the dim layer.
    if (this.tutorial.active) {
      if (input.wasPressed('escape')) this.tutorial.handleKey('escape', this.profile);
      else if (input.wasPressed('enter')) this.tutorial.handleKey('enter', this.profile);
      else if (input.wasPressed('space')) this.tutorial.handleKey('space', this.profile);
      else if (input.wasPressed('arrowright')) this.tutorial.handleKey('arrowright', this.profile);
      else if (input.wasPressed('arrowleft')) this.tutorial.handleKey('arrowleft', this.profile);
      if (input.wasClicked()) {
        const pos = input.clickPos();
        this.tutorial.handleClick(pos.x, pos.y, this.profile);
      }
      return;
    }

    // While the Profile / Scoreboard overlay is open it claims all input
    // (§19.3) — P or Esc closes it, clicks route to its tabs / unlock nodes.
    if (this.profileScreenOpen) {
      if (input.wasPressed('p') || input.wasPressed('escape')) {
        this.profileScreenOpen = false;
      } else if (input.wasClicked()) {
        this.handleProfileClick(input.clickPos());
      }
      return;
    }
    // Sprint 62 (auth-frontend-impl-s62) — first-visit start-screen modal
    // preempts every game control while `Profile.guestMode === null`. The
    // cookies banner is non-modal but tested first so a click on its
    // Accept / Just-essentials buttons still resolves while the modal is
    // up (banner sits at the bottom of the canvas, modal centred — no
    // overlap). Esc is intentionally absorbed — the modal is
    // dismiss-by-choice only. See docs/AUTH_DESIGN.md §3.
    if (isStartScreenOpen(this.profile)) {
      if (input.wasClicked()) {
        const pos = input.clickPos();
        if (isCookiesBannerOpen(this.profile)) {
          const cookies = cookiesBannerHit(pos.x, pos.y);
          if (cookies) {
            acceptCookiesChoice(
              this.profile,
              cookies.kind === 'accept' ? 'accepted' : 'essentials',
              this.profileName,
            );
            return;
          }
        }
        const hit = startScreenHit(pos.x, pos.y);
        if (hit) this.handleStartScreenHit(hit);
      }
      return;
    }
    // Sprint 62 — cookies banner remains visible after the start-screen
    // modal is dismissed if the player chose "Play as guest" without
    // first touching either banner button. While it is up the two banner
    // buttons capture clicks before any other UI underneath. Other
    // gestures (keyboard hotkeys, non-banner clicks) fall through to the
    // legacy paths.
    if (isCookiesBannerOpen(this.profile) && input.wasClicked()) {
      const pos = input.clickPos();
      const cookies = cookiesBannerHit(pos.x, pos.y);
      if (cookies) {
        acceptCookiesChoice(
          this.profile,
          cookies.kind === 'accept' ? 'accepted' : 'essentials',
          this.profileName,
        );
        return;
      }
    }
    // Sprint 57 (puzzle-enable-s57) — while the puzzle level-select overlay
    // is visible (the player picked the title PUZZLE chip but has not yet
    // loaded a level) it claims all input. Esc backs out to Campaign;
    // clicks route to `puzzleSelectHit` (close button or a level card).
    // Other hotkeys are absorbed so the player can't arm a tower / start a
    // wave through a dimmed title screen.
    if (this.isPuzzleSelectOpen()) {
      if (input.wasPressed('escape')) {
        this.setMode('campaign');
      } else if (input.wasClicked()) {
        this.handlePuzzleSelectClick(input.clickPos());
      }
      return;
    }
    // Sprint 63 (puzzle-ux-briefing-s63) — the briefing card is modal once
    // a puzzle level is loaded and the player has not yet pressed Start.
    // Enter/Space dismisses; Esc bounces back to the level-select overlay.
    if (this.isPuzzleBriefingOpen()) {
      if (input.wasPressed('escape')) {
        this.state.puzzleBriefingPending = false;
        this.state.puzzleLevelId = null;
        this.state.losWalls = null;
      } else if (input.wasPressed('enter') || input.wasPressed('space')) {
        this.dismissPuzzleBriefing();
      } else if (input.wasClicked()) {
        this.handlePuzzleBriefingClick(input.clickPos());
      }
      return;
    }
    // Sprint 63 — Win / Lose overlay is modal in puzzle mode. Click routes
    // to the primary (Next / Reset) or Back button; Esc backs out to the
    // level-select overlay.
    if (this.isPuzzleResultOpen()) {
      if (input.wasPressed('escape')) {
        this.reset(this.seed, 'puzzle', this.difficulty);
      } else if (input.wasClicked()) {
        this.handlePuzzleResultClick(input.clickPos());
      }
      return;
    }
    // P opens the Profile / Scoreboard screen — between runs only (§19.3).
    if (input.wasPressed('p') && canOpenProfile(this.state)) {
      this.openProfile(this.state.phase === 'build' ? 'unlocks' : 'scoreboard');
      return;
    }

    // While an onboarding modal (first-run intro / Help panel) is open it
    // claims all input — game hotkeys and clicks are suppressed underneath it.
    if (this.onboarding.modalOpen) {
      if (input.anyPressed()) {
        const k = input.wasPressed('escape')
          ? 'escape'
          : input.wasPressed('h')
            ? 'h'
            : input.wasPressed('?')
              ? '?'
              : 'any';
        this.onboarding.handleKey(k);
      }
      if (input.wasClicked()) {
        const p = input.clickPos();
        this.onboarding.handleClick(p.x, p.y);
      }
      return;
    }

    // H / ? opens the Help panel.
    if (input.wasPressed('h') || input.wasPressed('?')) {
      this.onboarding.openHelp();
      return;
    }

    if (input.wasPressed('1')) this.armTower('pulse');
    if (input.wasPressed('2')) this.armTower('arc');
    if (input.wasPressed('3')) this.armTower('pylon');
    if (input.wasPressed('4')) this.armTower('cryo');
    if (input.wasPressed('5')) this.armTower('mortar');
    if (input.wasPressed('6')) this.armTower('sniper');
    // 7 arms the Maze-Mode Wall (docs/GAME_DESIGN.md §16.9).
    if (input.wasPressed('7')) this.armTower('wall');

    if (input.wasPressed('space')) {
      if (s.phase === 'build') startWave(s, this.audio);
      else if (s.phase === 'wave') s.paused = !s.paused;
    }
    if (input.wasPressed('f')) this.setSpeed(s.speed === 1 ? 2 : 1);
    if (input.wasPressed('u')) this.upgradeSelected();
    if (input.wasPressed('t')) this.cycleTargetSelected();
    if (input.wasPressed('s')) this.sellSelected();
    // Sprint 26 (REC 3, docs/REC3_DESIGN.md §4.3): `O` toggles per-tower
    // Overclock on the currently inspected weapon tower — the in-context
    // shortcut for the selection-panel chip.
    if (input.wasPressed('o')) this.toggleOverclockSelected();

    // Active abilities (§15): Q/E fire instantly, W arms the Airstrike target.
    // Airstrike / EMP Burst are unlock-gated for the player; Overcharge is the
    // starter ability. (The `useAbility` test hook stays unlock-agnostic.)
    if (input.wasPressed('q')) useAbility(s, 'overcharge', this.audio);
    if (input.wasPressed('w')) this.armAirstrike();
    if (input.wasPressed('e')) {
      if (this.gateAbility('empBurst')) useAbility(s, 'empBurst', this.audio);
    }
    // Overload dial (§8): [ lowers / ] raises the next wave's tier (gated).
    if (input.wasPressed('[')) this.playerSetOverload(s.overload - 1);
    if (input.wasPressed(']')) this.playerSetOverload(s.overload + 1);

    if (input.wasPressed('escape')) {
      s.selectedTowerType = null;
      s.selectedTowerId = null;
      this.pendingAirstrike = false;
    }
    if (input.wasPressed('m')) s.muted = this.audio.toggleMute();
    if (input.wasPressed('r')) this.reset(this.seed);

    if (input.wasClicked()) this.handleClick(input.clickPos());
  }

  /**
   * Arm a tower type for placement (cancels any tower inspection). The UI-only
   * unlock gate (§19) blocks a locked tower for the player; the `placeTower`
   * test hook stays unlock-agnostic.
   */
  private armTower(type: TowerType): void {
    if (!isTowerUnlocked(this.profile, type)) {
      notifyLocked(
        this.state,
        ti18n('hud.lockedTowerNotice', { tower: ti18n(`towers.${type}.label`) }),
      );
      return;
    }
    // Sprint 63 (puzzle-ux-briefing-s63) — in puzzle mode the level's
    // `allowedTowers` whitelist is the placement gate. A forbidden tower
    // can never be armed via hotkey / chip; posting a notice keeps the
    // player on signal.
    const s = this.state;
    if (s.mode === 'puzzle' && s.puzzleLevelId) {
      const level = getPuzzleLevel(s.puzzleLevelId);
      if (level && !level.allowedTowers.includes(type)) {
        notifyLocked(
          this.state,
          ti18n('hud.lockedTowerNotice', { tower: ti18n(`towers.${type}.label`) }),
        );
        return;
      }
    }
    s.selectedTowerType = type;
    s.selectedTowerId = null;
    this.pendingAirstrike = false;
  }

  /**
   * Arm the Airstrike ability — the next playfield click picks its target
   * tile. Only meaningful in the wave phase (abilities are wave-only, §15).
   * Unlock-gated for the player.
   */
  private armAirstrike(): void {
    if (this.state.phase !== 'wave') return;
    if (!this.gateAbility('airstrike')) return;
    this.pendingAirstrike = true;
    this.state.selectedTowerType = null;
    this.state.selectedTowerId = null;
  }

  /**
   * UI-only unlock gate for a player-triggered ability — true when the profile
   * owns it. A locked ability posts a notice and is blocked; the `useAbility`
   * test hook bypasses this entirely (unlock-agnostic, §19.5).
   */
  private gateAbility(id: AbilityId): boolean {
    if (isAbilityUnlocked(this.profile, id)) return true;
    notifyLocked(this.state, ti18n('hud.lockedAbility'));
    return false;
  }

  /**
   * Player-facing Overload set — clamps the requested tier to the profile's
   * unlocked ceiling (`maxOverloadTier`). The `setOverload` test hook is
   * unlock-agnostic and reaches every tier (§19.5).
   *
   * Sprint 53 (tutorial-flow-s53) — the first time the player dials past
   * tier 0, the Overload context hint fires once per profile. The hook is a
   * no-op for the tier-0 default and for subsequent crank-ups (the seen flag
   * short-circuits the second-and-later calls), so dial-down / dial-up
   * cycles never re-trigger.
   */
  private playerSetOverload(tier: number): void {
    const capped = Math.min(tier, maxOverloadTier(this.profile));
    if (tier > capped && tier <= CONFIG.overloadTiers.length - 1) {
      notifyLocked(this.state, ti18n('hud.lockedOverload'));
    }
    setOverload(this.state, capped);
    this.onboarding.noteOverloadDial(this.profile, capped);
  }

  /** Player-facing mode select — gated by the unlock tree (§19). */
  private playerSetMode(mode: GameMode): void {
    // Sprint 30 — puzzle mode is opened from its own level-select overlay (§puzzle),
    // never from the campaign/endless/maze chip row. The unlock tree keys on
    // `RunMode` (campaign/endless/maze) only; puzzle is unlock-free in MVP.
    if (mode !== 'puzzle' && !isModeUnlocked(this.profile, mode)) {
      // Sprint 69 — pre-localise `mode` so the notice reads "Endless mode
      // locked" in the player's language instead of "endless mode locked".
      notifyLocked(this.state, ti18n('hud.lockedMode', { mode: ti18n(`modes.${mode}`) }));
      return;
    }
    this.setMode(mode);
  }

  /** Player-facing difficulty select — gated by the unlock tree (§19). */
  private playerSetDifficulty(difficulty: Difficulty): void {
    if (!isDifficultyUnlocked(this.profile, difficulty)) {
      notifyLocked(this.state, ti18n('hud.lockedDifficulty'));
      return;
    }
    this.setDifficulty(difficulty);
  }

  /** Open the Profile / Scoreboard overlay on `tab`. */
  private openProfile(tab: ProfileTab): void {
    this.profileScreenOpen = true;
    this.profileTab = tab;
  }

  /**
   * Sprint 62 (auth-frontend-impl-s62) — resolve a start-screen modal
   * choice. Discord / Google / Email currently route to a placeholder
   * "Coming soon" HUD notice; the backend (Cognito + IdP federation)
   * ships in `auth-backend-s63`. The Play-as-guest path persists
   * `guestMode = true` on the profile, which closes the modal on the
   * next frame via `isStartScreenOpen`.
   *
   * Mirrors the `puzzleSelectHit` dispatch pattern — the modal layer
   * returns a tagged union; this orchestrator method routes each kind
   * to the right side effect. Kept tiny so the future backend swap is
   * a one-call change at the three sign-in branches.
   */
  private handleStartScreenHit(hit: StartHit): void {
    if (hit.kind === 'guest') {
      acceptGuestMode(this.profile, this.profileName);
      return;
    }
    const stub =
      hit.kind === 'discord'
        ? signInWithDiscord()
        : hit.kind === 'google'
          ? signInWithGoogle()
          : signInWithEmail();
    notifyLocked(this.state, stub.placeholder);
  }

  /**
   * Sprint 62 — explicit Sign-out reachable from the Profile screen.
   * Resets `Profile.guestMode` to `null` so the start-screen modal
   * re-opens on the next frame. No-op for guests / fresh-modal players.
   * Today the backend session-cookie clear is stubbed (see `signOut`
   * docs in `src/auth/start.ts`).
   */
  signOut(): void {
    signOutAuth(this.profile, this.profileName);
    // Close the Profile screen so the start-screen modal takes the foreground.
    this.profileScreenOpen = false;
  }

  /**
   * Sprint 57 (puzzle-enable-s57) — true while the puzzle level-select
   * overlay should be on-screen. Open is purely a function of the current
   * state shape: we're in puzzle mode on the wave-0 title screen with no
   * level loaded yet. As soon as `setPuzzleLevel(id)` populates
   * `puzzleLevelId`, the overlay closes; if the player picks a different
   * mode (or hits Esc → Campaign) we exit puzzle mode entirely and the
   * overlay closes the same way. No new flag in `Game` — the predicate is
   * the source of truth, mirroring the title screen's own openness.
   */
  private isPuzzleSelectOpen(): boolean {
    const s = this.state;
    return (
      s.mode === 'puzzle' &&
      s.phase === 'build' &&
      s.wave === 0 &&
      s.puzzleLevelId === null
    );
  }

  /**
   * Sprint 63 (puzzle-ux-briefing-s63) — true while the **briefing card**
   * for the loaded puzzle level should sit on top of the playfield. Open is
   * a pure function of `state.puzzleBriefingPending` (the briefing flag is
   * the single source of truth — set by `loadPuzzleLevel`, cleared by
   * Start). Implicitly gated on puzzle mode + a loaded level — those are
   * preconditions for `puzzleBriefingPending` ever being true.
   */
  private isPuzzleBriefingOpen(): boolean {
    return this.state.puzzleBriefingPending;
  }

  /**
   * Sprint 63 — true while the puzzle Win / Lose overlay should sit on top
   * of the playfield. Only meaningful in puzzle mode; the legacy modes draw
   * their own `won` / `lost` overlay through `drawHud`'s `drawOverlay`.
   */
  private isPuzzleResultOpen(): boolean {
    const s = this.state;
    return s.mode === 'puzzle' && (s.phase === 'won' || s.phase === 'lost');
  }

  /**
   * Sprint 63 — route a click while the briefing card is open. Start
   * dismisses the card; Cancel exits puzzle mode back to the level-select
   * overlay (mirrors the level-select Close button's path). Dead-zone clicks
   * are absorbed — the briefing is modal.
   */
  private handlePuzzleBriefingClick(pos: { x: number; y: number }): void {
    const hit = puzzleBriefingHit(pos.x, pos.y);
    if (!hit) return;
    if (hit.kind === 'start') {
      this.dismissPuzzleBriefing();
    } else {
      // Cancel — back to the level-select overlay. `setPuzzleLevel` re-loads
      // the player's choice from there; clearing `puzzleLevelId` re-opens the
      // overlay through `isPuzzleSelectOpen()`.
      this.state.puzzleBriefingPending = false;
      this.state.puzzleLevelId = null;
      this.state.losWalls = null;
    }
  }

  /**
   * Sprint 63 — flip the briefing flag off (the player pressed Start, or QA
   * is stepping through the briefing flow via the test hook).
   */
  private dismissPuzzleBriefing(): void {
    this.state.puzzleBriefingPending = false;
  }

  /**
   * Sprint 63 — route a click on the puzzle Win / Lose overlay. Primary
   * advances to the next level (won, when one exists), retries (won + no
   * next, or replay), or resets the current level (lost). Back returns to
   * the level-select overlay. Dead-zone clicks are absorbed.
   */
  private handlePuzzleResultClick(pos: { x: number; y: number }): void {
    const hit = puzzleResultHit(pos.x, pos.y);
    if (!hit) return;
    const s = this.state;
    const won = s.phase === 'won';
    const levelId = s.puzzleLevelId;
    if (hit.kind === 'back') {
      // Bounce back to the level-select overlay via the standard puzzle-mode
      // entry (a clean reset re-creates the wave-0 build state).
      this.reset(this.seed, 'puzzle', this.difficulty);
      return;
    }
    // primary
    if (!levelId) return;
    if (won) {
      const idx = PUZZLES.findIndex((l) => l.id === levelId);
      const next = idx >= 0 ? PUZZLES[idx + 1] : undefined;
      if (next) {
        this.setPuzzleLevel(next.id);
      } else {
        // No next level — replay the current one.
        this.setPuzzleLevel(levelId);
      }
    } else {
      // Lost — reset the current attempt.
      this.setPuzzleLevel(levelId);
    }
  }

  /**
   * Sprint 57 — route a click while the puzzle level-select overlay is
   * open. The overlay's `puzzleSelectHit` returns one of three results:
   *   - `null` — dead-zone click; absorbed (the overlay is modal).
   *   - `{ kind: 'close' }` — exit puzzle mode back to Campaign.
   *   - `{ kind: 'level', id }` — load the picked level via the existing
   *     `setPuzzleLevel` path (sets the synthetic map, gold budget, LOS
   *     walls and `puzzleLevelId`, which closes the overlay on the next
   *     render).
   */
  private handlePuzzleSelectClick(pos: { x: number; y: number }): void {
    const hit = puzzleSelectHit(pos.x, pos.y);
    if (!hit) return;
    if (hit.kind === 'close') {
      this.setMode('campaign');
      return;
    }
    if (hit.kind === 'page') {
      // Pagination state lives in puzzleSelect.ts; the hit-test already
      // mutated `currentPage`. We just need to repaint — the orchestrator's
      // next frame will pick up the new page.
      return;
    }
    this.setPuzzleLevel(hit.id);
  }

  /** Route a click inside the open Profile / Scoreboard overlay. */
  private handleProfileClick(pos: { x: number; y: number }): void {
    // Sprint 62 — pass the profile so the Sign-out hit can fire when the
    // player is signed in. Guests see no Sign-out button at all, so the
    // hit-test legitimately never returns `'signout'` for them today.
    const hit = profileScreenHit(pos.x, pos.y, this.profileTab, this.profile);
    if (!hit) return;
    if (hit.kind === 'close') {
      this.profileScreenOpen = false;
    } else if (hit.kind === 'tab') {
      this.profileTab = hit.tab;
    } else if (hit.kind === 'signout') {
      // Sprint 62 — reset the modal gate; next render re-opens the
      // start-screen modal. The audio cue mirrors the unlock-revert
      // "sell" beat so the player gets a confirmation tone.
      this.signOut();
      this.audio.play('sell');
    } else {
      // An unlock-node click — purchase it if affordable + prereqs met (§19.2).
      if (purchaseUnlock(this.profile, hit.id)) {
        saveProfile(this.profile, this.profileName);
        this.audio.play('upgrade');
      } else {
        this.audio.play('error');
      }
    }
  }

  /** Sell whichever tower is currently inspected. */
  private sellSelected(): void {
    const tower = this.state.towers.find((t) => t.id === this.state.selectedTowerId);
    if (tower) sellTower(this.state, tower.col, tower.row, this.audio);
  }

  /** Upgrade whichever tower is currently inspected. */
  private upgradeSelected(): void {
    const tower = this.state.towers.find((t) => t.id === this.state.selectedTowerId);
    if (tower) upgradeTower(this.state, tower.col, tower.row, this.audio);
  }

  /** Cycle the targeting mode of whichever weapon tower is inspected. */
  private cycleTargetSelected(): void {
    const tower = this.state.towers.find((t) => t.id === this.state.selectedTowerId);
    if (tower) cycleTowerTargetMode(this.state, tower.col, tower.row);
  }

  /**
   * Sprint 26 (REC 3, docs/REC3_DESIGN.md §4.3) — flip Overclock on
   * whichever weapon tower is inspected. Quiet no-op on the Pylon / Wall (no
   * fire loop); posts an HUD notice on a real flip so the player gets a
   * confirmation alongside the orange halo + `OC ×N` pip.
   */
  private toggleOverclockSelected(): void {
    const tower = this.state.towers.find((t) => t.id === this.state.selectedTowerId);
    if (!tower) return;
    if (!CONFIG.towers[tower.type].weapon) return;
    const next = systemsToggleOverclock(this.state, tower.id);
    if (next === null) return;
    // Sprint 69 (i18n-gaps-extract-s69) — route through the catalogue
    // so the confirmation toast reads in the player's language.
    notifyLocked(
      this.state,
      ti18n(next ? 'hud.overclockNoticeOn' : 'hud.overclockNoticeOff', {
        tower: ti18n(`towers.${tower.type}.label`),
      }),
    );
    this.audio.play(next ? 'upgrade' : 'sell');
  }

  /**
   * Route a pointer click. Precedence (§6.5): the `?` Help button, the
   * title-screen / win-screen overlays, the contextual selection panel, the
   * COMMAND DOCK (BUILD chips → WAVE controls → ABILITIES zone), then the
   * playfield tile. The Profile launcher overlay is tested first of all.
   */
  private handleClick(pos: { x: number; y: number }): void {
    const s = this.state;
    // The Profile launcher (title screen + game-over) overlays everything.
    if (profileLauncherAt(pos.x, pos.y, s)) {
      this.openProfile(s.phase === 'build' ? 'unlocks' : 'scoreboard');
      return;
    }
    // 1. The `?` Help button (READOUT BAR corner).
    if (helpButtonAt(pos.x, pos.y)) {
      this.onboarding.openHelp();
      return;
    }
    // Sprint 73 (tutorial-impl-s73) — the title-screen Start Tutorial button.
    // Gated on `isTitleScreen` inside `tutorialButtonAt`, so this never
    // matches outside wave 0 build phase. Click launches the 12-step tour.
    if (tutorialButtonAt(pos.x, pos.y, s)) {
      this.startTutorial();
      return;
    }
    // 2. Title-screen mode / difficulty chips + the win-screen Continue button.
    const mode = modeToggleAt(pos.x, pos.y, s);
    if (mode) {
      this.playerSetMode(mode);
      return;
    }
    const difficulty = difficultyToggleAt(pos.x, pos.y, s);
    if (difficulty) {
      this.playerSetDifficulty(difficulty);
      return;
    }
    if (winContinueAt(pos.x, pos.y, s)) {
      this.playerSetMode('endless');
      return;
    }
    // Sprint 21 — title-screen Map chip cycles `CONFIG.maps`. Drawn locally
    // (out of hud.ts to respect the OWN-zone constraint), so it is routed
    // here next to the mode / difficulty chips it visually pairs with.
    //
    // Sprint 76 (modal-occlusion-fix-s76) — while a tower is armed for
    // placement, this chip (anchored at y≈522, deep in rows 10-11 of the
    // playfield) becomes click-pass-through so the player can place the
    // armed tower on the row beneath it. See {@link titleSelectorsDimmed}.
    if (isTitleScreen(s) && !titleSelectorsDimmed(s) && mapChipAt(pos.x, pos.y)) {
      this.cycleMap();
      return;
    }
    // i18n — the title-screen LANG chip. Click cycles through the 12
    // shipped locales (`LOCALES`) — see `applyLocaleChipClick()` in hud.ts.
    if (localeChipAt(pos.x, pos.y, s)) {
      applyLocaleChipClick();
      return;
    }
    // 3. The contextual selection panel — tested before playfield routing so a
    //    panel click never falls through to the tile beneath it (§5.4).
    const selAction = selectionPanelButtonAt(pos.x, pos.y, s);
    if (selAction) {
      if (selAction === 'upgrade') this.upgradeSelected();
      else if (selAction === 'sell') this.sellSelected();
      else if (selAction === 'overclock') this.toggleOverclockSelected();
      else this.cycleTargetSelected();
      return;
    }
    // 4. The COMMAND DOCK — BUILD chips / WAVE controls, then the ABILITIES
    //    zone (the Overload dial in build, the ability slots in wave).
    const button = hudButtonAt(pos.x, pos.y, s.mode);
    if (button) {
      this.handleHudButton(button);
      return;
    }
    const overload = overloadPanelAt(pos.x, pos.y, s);
    if (overload) {
      if (overload.kind === 'minus') this.playerSetOverload(s.overload - 1);
      else if (overload.kind === 'plus') this.playerSetOverload(s.overload + 1);
      else this.playerSetOverload(overload.tier);
      return;
    }
    const ability = abilitySlotAt(pos.x, pos.y, s);
    if (ability) {
      this.triggerAbility(ability);
      return;
    }
    // A click anywhere in the HUD strip that hit no control is consumed.
    if (pos.y < CONFIG.hudHeight) return;
    // 5. The playfield tile.
    this.handlePlayfieldClick(pos.x, pos.y - CONFIG.hudHeight);
  }

  /** Route an ABILITIES-zone ability-slot click, mirroring the Q / W / E keys. */
  private triggerAbility(id: AbilityId): void {
    if (id === 'overcharge') useAbility(this.state, 'overcharge', this.audio);
    else if (id === 'airstrike') this.armAirstrike();
    else if (this.gateAbility('empBurst')) useAbility(this.state, 'empBurst', this.audio);
  }

  /** Apply a COMMAND DOCK button press (a BUILD chip or a WAVE control). */
  private handleHudButton(id: HudButtonId): void {
    const s = this.state;
    const tower = towerOfButton(id);
    if (tower) {
      this.armTower(tower);
      return;
    }
    switch (id) {
      case 'start':
        if (s.phase === 'build') startWave(s, this.audio);
        else if (s.phase === 'wave') s.paused = !s.paused;
        else this.reset(this.seed);
        break;
      case 'speed':
        this.setSpeed(s.speed === 1 ? 2 : 1);
        break;
      case 'mute':
        s.muted = this.audio.toggleMute();
        break;
      case 'tower-pulse':
      case 'tower-arc':
      case 'tower-cryo':
      case 'tower-pylon':
      case 'tower-mortar':
      case 'tower-sniper':
      case 'tower-wall':
        break;
    }
  }

  /** Handle a click on a playfield tile: airstrike target, place, select, or deselect. */
  private handlePlayfieldClick(localX: number, localY: number): void {
    const s = this.state;
    const { col, row } = pointToCell(localX, localY);
    // An armed Airstrike consumes the next playfield click as its target tile.
    if (this.pendingAirstrike) {
      this.pendingAirstrike = false;
      useAbility(s, 'airstrike', this.audio, col, row);
      return;
    }
    if (!inBounds(col, row)) return;

    const existing = towerAt(s, col, row);
    if (s.selectedTowerType) {
      if (existing) {
        s.selectedTowerId = existing.id;
        s.selectedTowerType = null;
      } else {
        const type = s.selectedTowerType;
        // Note successful first placement of each tower type for the
        // role-tour hint (Sprint 25, tutorial-s25). The hook itself is a
        // no-op on subsequent placements and on disabled onboarding.
        if (placeTower(s, type, col, row, this.audio)) {
          this.onboarding.noteFirstPlacement(this.profile, type);
        }
      }
      return;
    }
    s.selectedTowerId = existing ? existing.id : null;
  }

  // --- rendering ------------------------------------------------------------

  private render(alpha: number): void {
    this.frame += 1;
    const ctx = this.renderer.context;
    this.renderer.clear(CONFIG.colors.background);

    const s = this.state;
    let progress = 1;
    if (s.phase === 'wave' && !s.paused) {
      progress = clamp((this.stepAccumulator + alpha) / this.fixedStepsPerTick(), 0, 1);
    }
    renderGame(ctx, s, this.input.pointer(), progress, this.audio);
    // Sprint 70 (mode-clarity-impl-s70) — pointer feeds the title-screen
    // RUN MODE hover info card. drawHud is otherwise pure paint of state.
    drawHud(ctx, s, this.profile, this.input.pointer());
    // Sprint 21 — the title-screen Map chip. Drawn over `renderGame` (so it
    // sits on top of the etched playfield) but under the Profile launcher,
    // which has overlay priority. Visible only on the wave-0 title screen.
    if (isTitleScreen(s)) this.drawMapChip(ctx);
    // The Profile launcher (a Flux readout + button) sits over the title /
    // game-over screens; the run-end Flux award rides the game-over overlay.
    drawProfileLauncher(ctx, s, this.profile, this.lastRunFlux);
    // Sprint 54 (cheat-profile-s54) — tiny `🔓 CHEAT` / `👤 <name>` pips in
    // the READOUT BAR strip, just left of the `?` Help button. Drawn last
    // over `drawHud` / `drawProfileLauncher` so they sit on top, but render
    // is a no-op on the default profile with no cheat (bit-for-bit Sprint-53).
    drawCheatProfilePips(ctx, this.cheatActive, this.profileName);
    this.onboarding.update(s, this.profile, this.input.pointer());
    this.onboarding.render(ctx, s, this.profile, this.input.pointer());
    // The full-screen Profile / Scoreboard overlay draws on top of everything.
    if (this.profileScreenOpen) {
      const justFinished = s.phase === 'won' || s.phase === 'lost';
      drawProfileScreen(ctx, this.profile, this.profileTab, justFinished, this.input.pointer());
    }
    // Sprint 57 (puzzle-enable-s57) — the puzzle level-select overlay sits
    // on top of the title screen + Profile launcher when the player has
    // dialed into puzzle mode but not yet picked a level. Drawn last among
    // the title-screen overlays so it covers the locale chip / mode chips
    // beneath it, mirroring its modal input precedence in {@link
    // handleInput}. The Resume prompt below has higher visual priority
    // still (a pending resume blocks all overlays).
    if (this.isPuzzleSelectOpen()) drawPuzzleSelect(ctx);
    // Sprint 63 — the briefing card stamps over the playfield for a freshly
    // loaded puzzle level (after `setPuzzleLevel`, before Start). Drawn over
    // the HUD so the player sees the briefing copy in full focus.
    if (this.isPuzzleBriefingOpen() && s.puzzleLevelId) {
      drawPuzzleBriefing(ctx, s.puzzleLevelId);
    }
    // Sprint 63 — the puzzle Win / Lose overlay replaces the legacy HUD
    // overlay when the puzzle wave terminates. Drawn last among the
    // mode-specific overlays so it covers the briefing-card / select
    // overlays underneath.
    if (this.isPuzzleResultOpen() && s.puzzleLevelId) {
      const goldSpent = this.lastPuzzleGoldSpent;
      drawPuzzleResult(
        ctx,
        s.puzzleLevelId,
        s.phase === 'won',
        {
          towersUsed: s.towers.length,
          goldSpent,
          livesRemaining: Math.max(0, s.lives),
          startingLives: s.startLives,
        },
        PUZZLES.findIndex((l) => l.id === s.puzzleLevelId) < PUZZLES.length - 1,
      );
    }
    // Sprint 62 (auth-frontend-impl-s62) — first-visit cookies banner +
    // start-screen modal. Drawn after every other overlay so they sit on
    // top, but before the Resume prompt (a saved run is functionally
    // impossible on a first visit, but the Resume prompt has higher
    // priority by convention — see docs/AUTH_DESIGN.md §3.1 fresh state).
    // The banner is non-modal and is drawn first so the modal's dim layer
    // lays over it visually (the banner sits at the bottom of the canvas,
    // the modal centred, so they coexist cleanly).
    if (isCookiesBannerOpen(this.profile)) drawCookiesBanner(ctx);
    if (isStartScreenOpen(this.profile)) drawStartScreen(ctx);
    // The Resume / New prompt for a saved live run from a prior page load —
    // drawn last so it captures input over every other overlay (Sprint 16).
    if (this.pendingResume) this.drawResumePrompt(ctx);
    // Sprint 73 (tutorial-impl-s73) — the 12-step opt-in tour overlay sits
    // ABOVE the Resume prompt because it can only launch from the title
    // screen (the Start Tutorial button is gated on `isTitleScreen`), and
    // a player who clicks Resume from the prompt will never have an active
    // tour anyway. Painted last so the dim layer + neon panel land on top
    // of every other UI surface; gated on `tutorial.active` internally.
    this.tutorial.render(ctx, performance.now() / 1000);
    this.updateMusic();
  }

  /**
   * Draw the title-screen "Resume previous run?" prompt — a small centred
   * dialog with two buttons. Minimal by design: a single panel in the
   * orchestrator, no new files in `ui/` (the OWN-zone constraint keeps it out
   * of `hud.ts` / `onboarding.ts` / `profileScreen.ts`). Geometry mirrors
   * {@link RESUME_PROMPT}.
   */
  private drawResumePrompt(ctx: CanvasRenderingContext2D): void {
    const saved = this.pendingResume;
    if (!saved) return;
    const r = RESUME_PROMPT;
    // Dim the canvas behind the dialog so the title screen reads as inactive.
    ctx.fillStyle = 'rgba(6, 10, 22, 0.78)';
    ctx.fillRect(0, 0, this.renderer.width, this.renderer.height);
    // Panel.
    ctx.fillStyle = '#0c1322';
    ctx.fillRect(r.panel.x, r.panel.y, r.panel.w, r.panel.h);
    ctx.strokeStyle = CONFIG.colors.accent;
    ctx.lineWidth = 2;
    ctx.strokeRect(r.panel.x + 0.5, r.panel.y + 0.5, r.panel.w - 1, r.panel.h - 1);
    // Title.
    ctx.fillStyle = CONFIG.colors.hudText;
    ctx.font = '20px "Chakra Petch", system-ui, sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(ti18n('resume.title'), r.panel.x + r.panel.w / 2, r.panel.y + 38);
    // Subtitle — a one-line summary of what the player would be resuming into.
    // Sprint 69 — pre-localise mode + difficulty chip names so the line reads
    // in the player's language; the `resume.detail` template carries the rest.
    const s = saved.state;
    const towers = Array.isArray(s.towers) ? s.towers.length : 0;
    const detail = ti18n('resume.detail', {
      mode: ti18n(`modes.${s.mode}`),
      difficulty: ti18n(`difficulty.${s.difficulty}`),
      wave: s.wave,
      towers,
      gold: s.gold,
      lives: s.lives,
    });
    ctx.fillStyle = CONFIG.colors.hudMuted;
    ctx.font = '13px "Share Tech Mono", ui-monospace, monospace';
    ctx.fillText(detail, r.panel.x + r.panel.w / 2, r.panel.y + 68);
    // Buttons.
    this.drawResumeButton(ctx, r.resume, ti18n('resume.resume'), CONFIG.colors.good);
    this.drawResumeButton(ctx, r.discard, ti18n('resume.newRun'), CONFIG.colors.warning);
    // Hotkey footer.
    ctx.fillStyle = CONFIG.colors.hudMuted;
    ctx.font = '11px "Share Tech Mono", ui-monospace, monospace';
    ctx.fillText(ti18n('resume.hint'), r.panel.x + r.panel.w / 2, r.panel.y + r.panel.h - 14);
  }

  /**
   * Sprint 21 — draw the title-screen Map chip (label + tagline, plus a hint
   * that clicking cycles the map). A compact panel anchored at {@link
   * MAP_CHIP}; coordinates are canvas-space. Title screen only.
   */
  private drawMapChip(ctx: CanvasRenderingContext2D): void {
    const r = MAP_CHIP;
    const map = activeMap();
    // Sprint 76 (modal-occlusion-fix-s76) — fade the chip to 30 % opacity
    // while a tower is armed for placement, mirroring the title-banner
    // selector fade. The hit-test in {@link handleClick} already passes the
    // click through to the playfield in this state; the visual fade is the
    // user-facing cue that the chip is temporarily inert.
    ctx.save();
    if (titleSelectorsDimmed(this.state)) ctx.globalAlpha = 0.3;
    // Panel.
    ctx.fillStyle = 'rgba(10, 18, 36, 0.86)';
    ctx.fillRect(r.x, r.y, r.w, r.h);
    ctx.strokeStyle = 'rgba(45, 226, 230, 0.70)';
    ctx.lineWidth = 1.5;
    ctx.strokeRect(r.x + 0.5, r.y + 0.5, r.w - 1, r.h - 1);
    // Label row.
    ctx.fillStyle = CONFIG.colors.hudMuted;
    ctx.font = '10px "Share Tech Mono", ui-monospace, monospace';
    ctx.textAlign = 'left';
    ctx.textBaseline = 'top';
    // Sprint 69 — reuse-bug fixes: `hud.map` and `hud.clickHint` were
    // already in the catalogue; the renderer was calling the literals.
    // Map label + tagline route through `t('maps.<id>.…')`.
    ctx.fillText(ti18n('hud.map'), r.x + 10, r.y + 7);
    ctx.fillStyle = CONFIG.colors.hudText;
    ctx.font = '15px "Chakra Petch", system-ui, sans-serif';
    ctx.fillText(ti18n(`maps.${map.id}.label`), r.x + 10, r.y + 19);
    // Tagline + cycle hint.
    ctx.fillStyle = CONFIG.colors.hudMuted;
    ctx.font = '10px "Share Tech Mono", ui-monospace, monospace';
    const tag = ti18n(`maps.${map.id}.tagline`).slice(0, 30);
    ctx.fillText(tag, r.x + 10, r.y + 38);
    ctx.fillStyle = 'rgba(45, 226, 230, 0.85)';
    ctx.textAlign = 'right';
    ctx.fillText(ti18n('hud.clickHint'), r.x + r.w - 10, r.y + 38);
    ctx.restore();
  }

  /** One Resume-prompt button — a coloured outline with centred label. */
  private drawResumeButton(
    ctx: CanvasRenderingContext2D,
    box: { x: number; y: number; w: number; h: number },
    label: string,
    color: string,
  ): void {
    ctx.fillStyle = '#101a30';
    ctx.fillRect(box.x, box.y, box.w, box.h);
    ctx.strokeStyle = color;
    ctx.lineWidth = 2;
    ctx.strokeRect(box.x + 0.5, box.y + 0.5, box.w - 1, box.h - 1);
    ctx.fillStyle = color;
    ctx.font = '15px "Chakra Petch", system-ui, sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(label, box.x + box.w / 2, box.y + box.h / 2);
  }

  /**
   * Drive the §18 procedural music loop from the current run state — a sparse
   * pad in the build phase, +bass/drums during a wave, +arp (and a faster
   * pulse at Kernel Panic) during a boss fight. Audio-layer only, gated by
   * `juiceEnabled`; the win / lose cue plays over silence once the run ends.
   */
  private updateMusic(): void {
    const s = this.state;
    if (!CONFIG.juiceEnabled || s.phase === 'won' || s.phase === 'lost') {
      this.audio.stopMusic();
      return;
    }
    let intensity = 0;
    let panic = false;
    if (s.phase === 'wave') {
      intensity = 1;
      for (const e of s.enemies) {
        if (e.type === 'kernel') {
          intensity = 2;
          if (e.phase >= 3) panic = true;
        }
      }
    }
    this.audio.updateMusic(intensity, panic);
  }

  // --- public / test surface ------------------------------------------------

  /** Set the simulation speed multiplier (1 or 2). */
  private setSpeed(speed: 1 | 2): void {
    this.state.speed = speed;
    this.stepAccumulator = 0;
  }

  /**
   * Set the run mode. Before a run starts (build phase, wave 0) this picks the
   * upcoming mode (Campaign / Endless / Maze). On a finished run's win screen
   * `setMode('endless')` continues into Endless waves — keeping the board,
   * gold, lives and score, with the next Start Wave generating wave 16. A
   * Campaign run flips to `endless`; a Maze run stays `maze` (the Maze-Endless
   * continuation — see docs/GAME_DESIGN.md §16.1).
   */
  setMode(mode: GameMode): boolean {
    const s = this.state;
    if (s.phase === 'won' && mode === 'endless') {
      // The winning campaign/maze segment has just been settled into the
      // profile (§19.5). Record its contribution as the settlement baseline so
      // the Endless continuation later settles only its OWN waves + score —
      // never re-counting this already-settled segment (docs/GAME_DESIGN.md
      // §14). `s.score` here is the raw run score at the win, before the
      // continuation accrues any more.
      s.settledWaves = s.wave;
      s.settledScore = s.score;
      if (s.mode === 'campaign') {
        this.mode = 'endless';
        s.mode = 'endless';
      }
      s.phase = 'build';
      s.finalScore = 0;
      this.applyBests();
      return true;
    }
    if (s.phase === 'build' && s.wave === 0) {
      const modeChanged = mode !== s.mode;
      this.mode = mode;
      s.mode = mode;
      // A pre-run mode switch re-resolves the starting economy: the §16.9 Maze
      // early-economy nudge is added when switching onto Maze and dropped when
      // switching off it. Skipped on a no-op re-pick of the current mode.
      // Sprint 30 — puzzle mode loads its budget via `setPuzzleLevel`, so the
      // pre-run mode switch onto 'puzzle' leaves gold at the legacy default;
      // the caller is expected to follow up with `setPuzzleLevel(id)` to
      // load a real level. Switching OUT of puzzle resolves normally.
      if (modeChanged && mode !== 'puzzle') s.gold = startingGoldFor(s.difficulty, mode);
      // Sprint 30 — clearing puzzle-only state when switching out of puzzle.
      if (modeChanged && s.mode !== 'puzzle') {
        s.losWalls = null;
        s.puzzleLevelId = null;
      }
      this.applyBests();
      // Maze Mode resolves its open-field A* route up front (§16.3).
      recomputePaths(s);
      return true;
    }
    return false;
  }

  /**
   * Sprint 30 — load puzzle level `id` for play. Side-effects: switches the
   * active map to the level's synthetic registry entry, sets the gold budget /
   * LOS-wall mask / authored path, and re-rolls the run state. Returns true
   * on success; false for an unknown id or while a run is in progress.
   *
   * Mirrors `setMap` in shape — same pre-run guard, same idempotence on a
   * re-pick of the current level. The simulation reads the loaded data via
   * the existing `placeTower` / `simTick` pipeline; no new sim primitives.
   */
  setPuzzleLevel(id: string): boolean {
    const level = getPuzzleLevel(id);
    if (!level) return false;
    // Sprint 63 — also permit reloading from a finished puzzle attempt
    // (the Win / Lose overlay's "Next level" / "Reset attempt" buttons hit
    // `setPuzzleLevel` from `phase === 'won' | 'lost'`).
    const allowed =
      (this.state.phase === 'build' && this.state.wave === 0) ||
      (this.state.mode === 'puzzle' &&
        (this.state.phase === 'won' || this.state.phase === 'lost'));
    if (!allowed) return false;
    // Resetting through 'puzzle' mode wipes any prior run; `loadPuzzleLevel`
    // then layers the level data onto the fresh state.
    this.mode = 'puzzle';
    this.state = createInitialState(this.difficulty, 'puzzle');
    this.state.seed = this.seed;
    loadPuzzleLevel(this.state, level);
    invalidateBackdrop();
    this.applyBests();
    this.state.muted = this.audio.isMuted();
    this.stepAccumulator = 0;
    this.pendingAirstrike = false;
    // Sprint 63 — reset the gold-spent snapshot so a re-load isn't carrying
    // the prior attempt's stat into the Win / Lose overlay.
    this.lastPuzzleGoldSpent = 0;
    return true;
  }

  /**
   * Sprint 21 — set the active map (`'default'` / `'cramped'` / any id in
   * `CONFIG.maps`). Only valid before a run starts (build phase, wave 0);
   * applying it re-binds the live map (path + blocked-tile set), drops the
   * renderer's baked backdrop, and re-rolls the initial state on the chosen
   * map. Returns true on effect; false for an unknown id or while a run is
   * in progress. See docs/GAME_DESIGN.md §3.
   */
  setMap(id: string): boolean {
    if (this.state.phase !== 'build' || this.state.wave !== 0) return false;
    if (!getMap(id)) return false;
    if (activeMapId() === id) return true;
    setActiveMap(id);
    invalidateBackdrop();
    // A pre-run map switch re-rolls the initial state so its `path`,
    // `mazePathLength` and `mapId` snap to the new map. Difficulty / mode /
    // seed are preserved.
    this.state = createInitialState(this.difficulty, this.mode);
    this.state.seed = this.seed;
    this.applyBests();
    this.state.muted = this.audio.isMuted();
    recomputePaths(this.state);
    this.stepAccumulator = 0;
    this.pendingAirstrike = false;
    return true;
  }

  /**
   * Cycle the active map through the `CONFIG.maps` registry (wraps round).
   * Driven by the title-screen Map chip — a single-click toggle for the
   * two-map roster; trivially scales when more maps are added.
   */
  private cycleMap(): void {
    const registry = CONFIG.maps;
    if (registry.length <= 1) return;
    const idx = registry.findIndex((m) => m.id === activeMapId());
    const next = registry[(Math.max(0, idx) + 1) % registry.length]!;
    this.setMap(next.id);
  }

  /**
   * Set the run difficulty. Only valid before a run starts (build phase,
   * wave 0); it re-resolves the starting gold / lives. Returns true on effect.
   */
  setDifficulty(difficulty: Difficulty): boolean {
    if (this.state.phase !== 'build' || this.state.wave !== 0) return false;
    this.difficulty = difficulty;
    // `createInitialState` resolves the mode-aware starting gold (the §16.9
    // Maze nudge included), so the mode is passed in up front.
    this.state = createInitialState(difficulty, this.mode);
    this.state.seed = this.seed;
    this.applyBests();
    this.state.muted = this.audio.isMuted();
    recomputePaths(this.state);
    return true;
  }

  /**
   * Set the targeting mode of the weapon tower on tile (col, row). Valid in
   * any phase; returns false if no tower is there or it is the Pylon.
   */
  setTargetMode(col: number, row: number, mode: TargetMode): boolean {
    return setTowerTargetMode(this.state, col, row, mode);
  }

  /** Restart the run from a fresh build phase. Optionally reseed / set mode / difficulty. */
  reset(seed?: number, mode?: GameMode, difficulty?: Difficulty): void {
    if (seed !== undefined) this.seed = seed >>> 0;
    if (mode !== undefined) this.mode = mode;
    if (difficulty !== undefined) this.difficulty = difficulty;
    this.rng.reseed(this.seed);
    this.state = createInitialState(this.difficulty, this.mode);
    this.state.seed = this.seed;
    this.applyBests();
    this.state.muted = this.audio.isMuted();
    // Maze Mode resolves its open-field A* route up front (§16.3).
    recomputePaths(this.state);
    this.stepAccumulator = 0;
    this.pendingAirstrike = false;
    // `reset()` restarts the run only — the profile (§19) persists by design.
    this.profileScreenOpen = false;
    // A fresh run invalidates any saved live run from this or a prior page
    // load: the player has chosen to start over (R, the Restart button, or a
    // test-driven reset). Also clears any unresolved resume prompt — picking
    // R counts as picking "New".
    this.pendingResume = null;
    clearSavedRun(this.profileName);
  }

  /** Build a plain-object snapshot for test assertions. */
  private snapshot(): GameSnapshot {
    const s = this.state;
    return {
      phase: s.phase,
      mode: s.mode,
      difficulty: s.difficulty,
      // Sprint 21 — active map id, so QA / save round-trip can verify the
      // chosen map. The live binding in `src/game/map.ts` is the authoritative
      // source; `state.mapId` mirrors it for the snapshot.
      mapId: s.mapId,
      wave: s.wave,
      overload: s.overload,
      waveOverload: s.waveOverload,
      gold: s.gold,
      lives: s.lives,
      score: s.score,
      finalScore: s.finalScore,
      highScore: s.highScore,
      bestWave: s.bestWave,
      tick: s.tick,
      paused: s.paused,
      muted: s.muted,
      speed: s.speed,
      overchargeTicksLeft: s.overchargeTicksLeft,
      aftershockTicksLeft: s.aftershockTicksLeft,
      // Sprint 20 (abilities-s20b): the live Airstrike scorch zones — copied
      // so a test cannot mutate the live list. Each entry is `{ col, row,
      // ticksLeft, radius }`. See docs/GAME_DESIGN.md §15.
      scorchZones: s.scorchZones.map((z) => ({ ...z })),
      drainTicksLeft: s.drainTicksLeft,
      abilities: s.abilities.map((a) => ({ ...a })),
      power: { ...s.power },
      // The live global route (§16.3) + its tile-count metric.
      path: s.path.map((c) => ({ col: c.col, row: c.row })),
      mazePathLength: s.mazePathLength,
      // Sprint 30 — puzzle-mode mirrors. `losWalls` is exposed as the packed
      // tile-index array (deterministic order: ascending) so QA assertions
      // are stable; `null` outside puzzle mode. `puzzleLevelId` mirrors the
      // active level id, or `null`.
      losWalls: s.losWalls ? Array.from(s.losWalls).sort((a, b) => a - b) : null,
      puzzleLevelId: s.puzzleLevelId,
      // Sprint 63 — briefing-card flag; QA flips through the briefing flow
      // via the `dismissPuzzleBriefing` test hook.
      puzzleBriefingPending: s.puzzleBriefingPending,
      enemies: s.enemies.map((e) => ({
        ...e,
        // `tile` is copied so a test can never mutate a route cell in place.
        tile: { col: e.tile.col, row: e.tile.row },
        statuses: {
          slow: e.statuses.slow ? { ...e.statuses.slow } : null,
          corrode: e.statuses.corrode ? { ...e.statuses.corrode } : null,
          // Sprint 17 (cryo-s17) added the Cryo `brittle` painter mark to
          // EnemyStatuses; mirror it through the snapshot so the unioned shape
          // round-trips cleanly. The cryo-s17 agent owns the field's semantics.
          brittle: e.statuses.brittle ? { ...e.statuses.brittle } : null,
          emp: e.statuses.emp ? { ...e.statuses.emp } : null,
        },
      })),
      towers: s.towers.map((t) => ({ ...t })),
      projectiles: s.projectiles.map((p) => ({
        ...p,
        from: { ...p.from },
        to: { ...p.to },
      })),
      // Sprint 72 — mirror selectedTowerId so QA can verify the
      // contextual selection panel is open / closed (the Sprint 72
      // tooltip-suppress fix keys off this field).
      selectedTowerId: s.selectedTowerId,
      // Sprint 76 (modal-occlusion-fix-s76) — mirror selectedTowerType so
      // QA can verify the "armed for placement" mode that drives the
      // title-selector fade + click-pass-through (see
      // {@link titleSelectorsDimmed} in src/ui/hud.ts).
      selectedTowerType: s.selectedTowerType,
    };
  }

  /** The deterministic API surface consumed by Playwright tests. */
  get testApi(): GameTestApi {
    const game = this;
    const advance = (n = 1): void => game.advanceTicks(n);
    return {
      version: CONTRACT_VERSION,
      get phase() {
        return game.state.phase;
      },
      get frame() {
        return game.frame;
      },
      config: CONFIG,
      get state() {
        return game.snapshot();
      },
      get profile() {
        return cloneProfile(game.profile);
      },
      getState: () => game.snapshot(),
      reset: (seed?: number, mode?: GameMode, difficulty?: Difficulty) =>
        game.reset(seed, mode, difficulty),
      tick: advance,
      step: advance,
      placeTower: (type, col, row) => {
        // Mirror the live-input path so the role-tour hook fires for QA-driven
        // placements too (Sprint 25, tutorial-s25). No contract bump — the hook
        // is a no-op when onboarding is disabled (the bootGame() default).
        const ok = placeTower(game.state, type, col, row, game.audio);
        if (ok) game.onboarding.noteFirstPlacement(game.profile, type);
        return ok;
      },
      sellTower: (col, row) => sellTower(game.state, col, row, game.audio),
      canPlace: (type, col, row) => canPlace(game.state, type, col, row),
      pathFrom: (col, row) => pathFrom(game.state, col, row),
      upgradeTower: (col, row) => upgradeTower(game.state, col, row, game.audio),
      // Sprint 26 (REC 3) — per-tower Overclock test hooks. Pure boolean
      // flips routed through systems.ts so the grid is recomputed
      // immediately; `setOverclock` is the idempotent sibling.
      toggleOverclock: (towerId: number) => systemsToggleOverclock(game.state, towerId),
      setOverclock: (towerId: number, on: boolean) =>
        systemsSetOverclock(game.state, towerId, on),
      setTargetMode: (col, row, mode) => game.setTargetMode(col, row, mode),
      startWave: () => {
        startWave(game.state, game.audio);
      },
      setOverload: (tier: number) => {
        // Mirror the live-input path so the Sprint-53 Overload context hint
        // fires for QA-driven dials too — same convention as `placeTower`
        // → `noteFirstPlacement`. The hook is a no-op when onboarding is
        // disabled (the bootGame() default), so existing tests are intact.
        const ok = setOverload(game.state, tier);
        game.onboarding.noteOverloadDial(game.profile, tier);
        return ok;
      },
      useAbility: (id: AbilityId, col?: number, row?: number) =>
        useAbility(game.state, id, game.audio, col, row),
      setSpeed: (speed: 1 | 2) => game.setSpeed(speed),
      setMode: (mode: GameMode) => game.setMode(mode),
      setMap: (id: string) => game.setMap(id),
      setDifficulty: (difficulty: Difficulty) => game.setDifficulty(difficulty),
      // Sprint 30 — puzzle mode test hooks. `setPuzzleLevel` mirrors the
      // player path (level-select chip → setPuzzleLevel). `puzzleLevels`
      // exposes the curriculum so QA can iterate it. `evaluatePuzzleAttempt`
      // is the pure headless evaluator — does NOT disturb the live game's
      // state (it spins up its own private GameState internally).
      setPuzzleLevel: (id: string) => game.setPuzzleLevel(id),
      // Sprint 63 — dismiss the briefing card from QA. Pure boolean flip;
      // safe to call when no briefing is open (no-ops).
      dismissPuzzleBriefing: () => {
        game.state.puzzleBriefingPending = false;
      },
      get puzzleLevels() {
        return PUZZLES.map((l) => ({ id: l.id, name: l.name, hint: l.hint }));
      },
      evaluatePuzzleAttempt: (id: string, attempt: readonly PuzzlePlacement[]): PuzzleOutcome | null => {
        const level = getPuzzleLevel(id);
        if (!level) return null;
        return evaluatePuzzleAttemptPure(level, attempt);
      },
      // Sprint 30 — direct surface for the pure LOS geometry primitive. Lets
      // Playwright unit-test the DDA without spinning up a level. Pure helper
      // for tests / tools; never called from production sim paths in this
      // sprint (integration into `systems.ts` is a follow-up).
      losBlocked: (
        walls: ReadonlyArray<{ col: number; row: number }> | ReadonlySet<number>,
        a: { x: number; y: number },
        b: { x: number; y: number },
      ) => {
        const packed = walls instanceof Set
          ? (walls as ReadonlySet<number>)
          : packLosWalls(walls as ReadonlyArray<{ col: number; row: number }>, CONFIG.grid.cols);
        return losBlocked(packed, a, b, CONFIG.grid.tileSize, CONFIG.grid.cols, CONFIG.grid.rows);
      },
      losFirstHitT: (
        walls: ReadonlyArray<{ col: number; row: number }> | ReadonlySet<number>,
        a: { x: number; y: number },
        b: { x: number; y: number },
      ) => {
        const packed = walls instanceof Set
          ? (walls as ReadonlySet<number>)
          : packLosWalls(walls as ReadonlyArray<{ col: number; row: number }>, CONFIG.grid.cols);
        return losFirstHitT(packed, a, b, CONFIG.grid.tileSize, CONFIG.grid.cols, CONFIG.grid.rows);
      },
      genWave: (w: number) =>
        genWave(game.state.seed, w, CONFIG.difficulties[game.state.difficulty].enemyCountMult),
      spawnBoss: () => spawnBoss(game.state),
      // --- meta-progression (§19) — never touches `state` or the sim --------
      setProfile: (patch: ProfilePatch) => {
        applyProfilePatch(game.profile, patch);
        saveProfile(game.profile, game.profileName);
        game.applyBests();
      },
      resetProfile: () => {
        game.profile = createDefaultProfile();
        saveProfile(game.profile, game.profileName);
        game.applyBests();
      },
      unlock: (id: string) => {
        const ok = purchaseUnlock(game.profile, id);
        if (ok) saveProfile(game.profile, game.profileName);
        return ok;
      },
      grantFlux: (n: number) => {
        game.profile.flux = Math.max(0, game.profile.flux + Math.trunc(n));
        saveProfile(game.profile, game.profileName);
      },
      // Sprint 54 (cheat-profile-s54) — `cheatUnlockAll` grants every node
      // in `CONFIG.unlocks` to the active profile + sets flux to a high
      // value, then persists. Pure unlock-gate bypass — gameplay / sim /
      // determinism untouched. Same effect as the `?cheat=unlock-all` URL
      // param (which calls this method on boot). See {@link Game.cheatUnlockAll}.
      cheatUnlockAll: () => game.cheatUnlockAll(),
      get cheatActive() {
        return game.cheatActive;
      },
      get profileName() {
        return game.profileName;
      },
      // i18n test hook — switch the active UI locale. A string outside the
      // shipped set is silently ignored (the i18n helper sanitises). The
      // simulation is untouched; only UI strings re-resolve via `t()`.
      setLocale: (id: string) => setLocale(id),
      // i18n test surface — read the active locale and the raw catalogues.
      // `t()` is reachable via the same module, but exposing it on the test
      // API lets a test reach it without a dynamic import.
      i18n: {
        get locale() {
          return getLocale();
        },
        t: (key: string, vars?: Record<string, string | number>) => ti18n(key, vars),
        // Catalogues for all 12 shipped locales (Sprint 58 i18n-10langs-s58).
        // The Sprint-56b two-locale shape (`{ en, cs }`) is preserved so prior
        // tests keep compiling; everything else is exposed via the full
        // `byId` map plus the canonical `locales` array.
        catalogues: i18nCatalogues as unknown as Record<string, Record<string, unknown>>,
        locales: i18nLocales as unknown as readonly string[],
      },
      // Sprint 73 (tutorial-impl-s73) — opt-in interactive tour test hooks.
      // `startTutorial` resets to wave-0 build and enters step 0 (mirrors
      // clicking the title-screen Start Tutorial button). `tutorialNext`
      // / `tutorialSkip` walk / abort, and a completion or skip flips
      // `profile.seenFullTutorial` exactly as in real play.
      startTutorial: () => game.startTutorial(),
      tutorialNext: () => game.advanceTutorial(),
      tutorialSkip: () => game.skipTutorial(),
      tutorial: {
        get active() {
          return game.tutorialState.active;
        },
        get step() {
          return game.tutorialState.step;
        },
      },
      pressKey: (key: string) => game.input.simulateKeyDown(key),
      releaseKey: (key: string) => game.input.simulateKeyUp(key),
      // --- run save / resume (Sprint 16) ----------------------------------
      // Live-run persistence test hooks. Mirror the Resume / New choice the
      // player makes on the title-screen overlay so QA can drive both paths
      // deterministically. `hasSavedRun` answers both the on-disk probe (true
      // between page loads after a save was written) and the in-memory
      // pendingResume state — either is a real "there is a save" condition.
      hasSavedRun: () => game.hasPendingResume() || hasSavedRunOnDisk(game.profileName),
      resumeSavedRun: () => game.resumeSavedRun(),
      discardSavedRun: () => game.discardSavedRun(),
    };
  }
}

/** Post a transient "locked" notice to the HUD (the UI-only unlock gate). */
function notifyLocked(state: GameState, text: string): void {
  state.notice = { text, ttl: CONFIG.noticeTicks };
}

/** Read an optional `?seed=<int>` override from the page URL. */
function readSeedFromUrl(): number | null {
  try {
    const raw = new URLSearchParams(window.location.search).get('seed');
    if (raw === null) return null;
    const n = Number.parseInt(raw, 10);
    return Number.isFinite(n) ? n >>> 0 : null;
  } catch {
    return null;
  }
}

/**
 * Sprint 54 (cheat-profile-s54) — read an optional `?profile=<name>` URL
 * override. Sanitises to `[a-z0-9_-]{1,32}`: lowercased, illegal characters
 * stripped, clamped to 32 chars. Returns `undefined` for the un-named default
 * (the legacy `overvolt.profile` / `overvolt.run` keys), or the sanitised
 * name. A param that sanitises to the empty string defaults to `'default'`
 * so a profile clearly exists. See docs/GAME_DESIGN.md §cheat-profile.
 */
function readProfileNameFromUrl(): string | undefined {
  try {
    const raw = new URLSearchParams(window.location.search).get('profile');
    if (raw === null) return undefined;
    const sanitised = raw.toLowerCase().replace(/[^a-z0-9_-]/g, '').slice(0, 32);
    return sanitised.length > 0 ? sanitised : 'default';
  } catch {
    return undefined;
  }
}

/**
 * Sprint 54 (cheat-profile-s54) — true when the URL carries `?cheat=unlock-all`.
 * The cheat path grants every `CONFIG.unlocks` node + sets `profile.flux` to a
 * high value on boot. See {@link Game.cheatUnlockAll}.
 */
function readCheatFromUrl(): boolean {
  try {
    return new URLSearchParams(window.location.search).get('cheat') === 'unlock-all';
  } catch {
    return false;
  }
}

/**
 * Sprint 21 — read an optional `?map=<id>` override from the page URL. An
 * unknown id is treated as "no override" (the caller falls back to the
 * default); valid ids include `'default'` and `'cramped'` — see
 * `src/game/map.ts` MAPS / `CONFIG.maps`.
 */
function readMapFromUrl(): string | null {
  try {
    const raw = new URLSearchParams(window.location.search).get('map');
    if (raw === null) return null;
    return getMap(raw) ? raw : null;
  } catch {
    return null;
  }
}

// --- Map indicator chip (Sprint 21) -----------------------------------------

/**
 * Canvas-space rectangle for the title-screen Map chip. Kept local to
 * `Game.ts` per the OWN-zone constraint (no new `ui/` files; the title-screen
 * chip row in `hud.ts` is owned by another agent). Sits in the lower-right
 * corner of the playfield area, above the COMMAND DOCK and clear of the
 * mode / difficulty chip rows. A click cycles through `CONFIG.maps` (modulo
 * the registry length) and restarts the run on the chosen map.
 */
const MAP_CHIP = { x: 588, y: 522, w: 196, h: 56 } as const;

/** True while the wave-0 title screen (mode / difficulty / map chips) shows. */
function isTitleScreen(state: GameState): boolean {
  return state.phase === 'build' && state.wave === 0;
}

/** Hit-test the title-screen Map chip — see {@link MAP_CHIP}. */
function mapChipAt(x: number, y: number): boolean {
  return (
    x >= MAP_CHIP.x &&
    x <= MAP_CHIP.x + MAP_CHIP.w &&
    y >= MAP_CHIP.y &&
    y <= MAP_CHIP.y + MAP_CHIP.h
  );
}

// --- Resume-prompt overlay geometry (Sprint 16) -----------------------------

/**
 * Canvas-space rectangles for the boot-time "Resume previous run?" dialog.
 * Centred over the canvas; the two buttons sit side-by-side. Kept local to
 * `Game.ts` per the OWN-zone constraint (no new `ui/` files for the prompt).
 */
const RESUME_PROMPT = {
  panel: { x: 220, y: 196, w: 360, h: 200 },
  resume: { x: 246, y: 312, w: 144, h: 44 },
  discard: { x: 410, y: 312, w: 144, h: 44 },
} as const;

/** Click hit-test: which Resume-prompt button (if any) was hit? */
function resumePromptHit(x: number, y: number): 'resume' | 'discard' | null {
  const inRect = (box: { x: number; y: number; w: number; h: number }): boolean =>
    x >= box.x && x <= box.x + box.w && y >= box.y && y <= box.y + box.h;
  if (inRect(RESUME_PROMPT.resume)) return 'resume';
  if (inRect(RESUME_PROMPT.discard)) return 'discard';
  return null;
}

